import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ChevronLeft } from 'lucide-react';

function StreetmojiAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get the data passed from the game (mojo, streak, etc.)
  const gameData = location.state || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Create new account
        if (!username.trim()) {
          setError('Username is required');
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Update profile with username
        await updateProfile(user, {
          displayName: username
        });

        // Get existing localStorage data
        const totalMojo = parseInt(localStorage.getItem('streetmoji_total_mojo') || '0');
        const streakData = JSON.parse(localStorage.getItem('streetmoji_streak') || '{"count":0,"lastPlayed":null}');

        // Create user document in Firestore with their current progress
        await setDoc(doc(db, 'streetmoji_users', user.uid), {
          username: username,
          email: email,
          totalMojo: totalMojo,
          streak: streakData.count,
          lastPlayed: streakData.lastPlayed,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });

        // Store user info in localStorage
        localStorage.setItem('streetmoji_user', JSON.stringify({
          uid: user.uid,
          email: user.email,
          username: username
        }));

        alert('Account created successfully! Your progress has been saved.');
        navigate('/streetmoji');
      } else {
        // Sign in
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Get user data from Firestore
        const userDoc = await getDoc(doc(db, 'streetmoji_users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Sync Firestore data to localStorage
          localStorage.setItem('streetmoji_total_mojo', userData.totalMojo.toString());
          localStorage.setItem('streetmoji_streak', JSON.stringify({
            count: userData.streak,
            lastPlayed: userData.lastPlayed
          }));
          
          // Store user info in localStorage
          localStorage.setItem('streetmoji_user', JSON.stringify({
            uid: user.uid,
            email: user.email,
            username: userData.username
          }));

          alert('Signed in successfully! Your progress has been synced.');
        } else {
          // User document doesn't exist, create it with current localStorage data
          const totalMojo = parseInt(localStorage.getItem('streetmoji_total_mojo') || '0');
          const streakData = JSON.parse(localStorage.getItem('streetmoji_streak') || '{"count":0,"lastPlayed":null}');

          await setDoc(doc(db, 'streetmoji_users', user.uid), {
            username: user.displayName || email.split('@')[0],
            email: user.email,
            totalMojo: totalMojo,
            streak: streakData.count,
            lastPlayed: streakData.lastPlayed,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });

          localStorage.setItem('streetmoji_user', JSON.stringify({
            uid: user.uid,
            email: user.email,
            username: user.displayName || email.split('@')[0]
          }));
        }

        navigate('/streetmoji');
      }
    } catch (error) {
      console.error('Auth error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered. Please sign in instead.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/weak-password':
          setError('Password should be at least 6 characters.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password.');
          break;
        default:
          setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(45deg, #1651D4, rgb(255, 255, 255))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <button
        onClick={() => navigate('/streetmoji')}
        style={{
          backgroundColor: 'lightgrey',
          color: 'black',
          opacity: '50%',
          padding: '5px',
          height: '40px',
          width: '40px',
          borderRadius: '20px',
          position: 'absolute',
          top: '20px',
          left: '20px',
          border: 'none',
          fontSize: '18px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <ChevronLeft style={{ marginTop: '3px' }} />
      </button>

      <div style={{
        background: 'rgba(0, 0, 0, 0.7)',
        padding: '40px',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '400px',
        boxSizing: 'border-box'
      }}>
        <img
          src="/OndaStreetMojiFinished.svg"
          alt="Onda Streetmoji"
          style={{
            width: '100%',
            display: 'block',
            margin: '0 auto 30px'
          }}
        />

        <h2 className="electronic" style={{
          fontSize: '1.8rem',
          color: '#FF9500',
          textAlign: 'center',
          fontWeight: '500',
          marginBottom: '10px'
        }}>
          {isSignUp ? 'Create Account' : 'Sign In'}
        </h2>

        <p style={{
          color: '#FF9500',
          textAlign: 'center',
          marginBottom: '30px',
          fontSize: '0.9rem',
          
        }}>
          Save your mojo and streak across devices
        </p>

        <form onSubmit={handleSubmit}>
          {isSignUp && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: '#FF9500',
                marginBottom: '8px',
                fontSize: '0.9rem'
              }}>
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required={isSignUp}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '2px solid #FF9500',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Choose a username"
              />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#FF9500',
              marginBottom: '8px',
              fontSize: '0.9rem'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '2px solid #FF9500',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
              placeholder="your@email.com"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#FF9500',
              marginBottom: '8px',
              fontSize: '0.9rem'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '2px solid #FF9500',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div style={{
              color: '#ff4444',
              background: 'rgba(255, 68, 68, 0.1)',
              padding: '10px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: '#FF9500',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1.1rem',
              fontWeight: '500',
               fontFamily: 'monospace',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginBottom: '15px'
            }}
          >
            {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>

          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }}
            style={{
              width: '100%',
              padding: '14px',
              background: 'transparent',
              color: '#FF9500',
              border: '0px solid #FF9500',
              borderRadius: '10px',
               fontFamily: 'monospace',
              fontSize: '.8rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
          </button>
        </form>

        {gameData.totalMojo > 0 && (
          <div style={{
            marginTop: '30px',
            padding: '15px',
            background: 'rgba(255, 149, 0, 0.1)',
            borderRadius: '10px',
            border: '1px solid #FF9500'
          }}>
            <p className="electronic" style={{
              color: '#FF9500',
              fontSize: '0.9rem',
              textAlign: 'center',
              margin: 0
            }}>
              Your current progress will be saved:
            </p>
            <p className="electronic" style={{
              color: '#FF9500',
              fontSize: '1.2rem',
              textAlign: 'center',
              margin: '10px 0 0 0'
            }}>
              {gameData.totalMojo} mojo • {gameData.streak} day streak
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default StreetmojiAuth;

